import { useEffect, useRef, useState } from 'react'
import { useEditor } from './store/editor'
import { getMe, loadProject } from './lib/api'
import { normalizeProject } from './lib/model'
import { readLocal, saveNow, setUrlProject, writeLocal } from './lib/persist'
import { importVaideoProject } from './lib/actions'
import TopBar from './components/TopBar'
import LeftPanel from './components/LeftPanel'
import Preview from './components/Preview'
import Inspector from './components/Inspector'
import Timeline from './components/Timeline'
import ExportDialog from './components/ExportDialog'
import ProjectsDialog from './components/ProjectsDialog'
import VoiceDialog from './components/VoiceDialog'
import { Busy, Toasts } from './components/Feedback'
import { useShortcuts } from './lib/shortcuts'
import { track } from './lib/analytics'
import { planOf } from './lib/plan'

async function boot() {
  const st = useEditor.getState()
  let user = null
  try { user = await getMe() } catch { /* offline / backend down */ }
  st.set({ user, authChecked: true })
  track('Editor Open', { plan: user ? planOf(user) : 'anon' })

  const params = new URL(window.location.href).searchParams
  const importUid = params.get('vaideoImport')
  const projectId = params.get('project')

  if (importUid) {
    if (!user) {
      st.toast('Inicia sesión (arriba a la derecha) y vuelve a pulsar «Abrir en el editor» en Vaideo', 'error')
      setUrlProject(null)
      return
    }
    await importVaideoProject(importUid)
    return
  }
  if (projectId && user) {
    st.set({ busy: 'Abriendo proyecto…' })
    try {
      const r = await loadProject(projectId)
      const p = normalizeProject(r.data)
      p.name = r.name || p.name
      useEditor.getState().setProject(p, { serverId: r.id })
      writeLocal()
    } catch (e) {
      st.toast(`No se pudo abrir el proyecto: ${(e as Error).message}`, 'error')
      setUrlProject(null)
    } finally {
      useEditor.getState().set({ busy: null })
    }
    return
  }
  const local = readLocal()
  if (local && local.project.clips.length) {
    useEditor.getState().setProject(local.project, { serverId: user ? local.serverId : null })
    useEditor.getState().set({ dirty: true })
    if (local.serverId && user) setUrlProject(local.serverId)
  }
}

export default function App() {
  const dialog = useEditor((s) => s.dialog)
  const [tlHeight, setTlHeight] = useState(() => Math.round(Math.max(240, Math.min(420, window.innerHeight * 0.36))))
  const booted = useRef(false)

  useEffect(() => {
    if (booted.current) return
    booted.current = true
    boot()
  }, [])

  // local autosave (every change) + account autosave (every 20 s when dirty)
  useEffect(() => {
    let t: number | undefined
    const unsub = useEditor.subscribe((s, prev) => {
      if (s.project !== prev.project || s.serverId !== prev.serverId) {
        clearTimeout(t)
        t = window.setTimeout(writeLocal, 600)
      }
    })
    const iv = window.setInterval(() => {
      const s = useEditor.getState()
      if (s.user && s.dirty && !s.saving && !s.playing && s.project.clips.length) saveNow({ silent: true })
    }, 20000)
    const onUnload = (e: BeforeUnloadEvent) => {
      writeLocal()
      const s = useEditor.getState()
      if (s.user && s.dirty && s.project.clips.length) {
        saveNow({ silent: true })
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onUnload)
    return () => { unsub(); clearInterval(iv); window.removeEventListener('beforeunload', onUnload) }
  }, [])

  useShortcuts()

  const startResize = (e: React.PointerEvent) => {
    const y0 = e.clientY
    const h0 = tlHeight
    const move = (ev: PointerEvent) => setTlHeight(Math.max(160, Math.min(window.innerHeight - 260, h0 - (ev.clientY - y0))))
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return (
    <div className="app">
      <TopBar />
      <div className="main">
        <LeftPanel />
        <Preview />
        <Inspector />
      </div>
      <div className="timeline" style={{ height: tlHeight }}>
        <div className="tl-resize" onPointerDown={startResize} />
        <Timeline />
      </div>
      {dialog === 'export' && <ExportDialog />}
      {dialog === 'projects' && <ProjectsDialog />}
      {dialog === 'voice' && <VoiceDialog />}
      <Toasts />
      <Busy />
      <div className="narrow-warn">
        <div>
          <h3>El editor necesita una pantalla más grande</h3>
          <p className="hint">Ábrelo en un ordenador o amplía la ventana para editar con la línea de tiempo.</p>
        </div>
      </div>
    </div>
  )
}
